import { gql, useQuery } from '@apollo/client';

import { isValidDealId } from './utils';
import type {
  GetSingleMonthRemittanceRecords,
  GetSingleMonthRemittanceRecordsVariables,
  GetSingleMonthRemittanceRecords_getSingleMonthRemittanceRecords_entities,
} from './__generated__/GetSingleMonthRemittanceRecords';

export const GET_SINGLE_MONTH_REMITTANCE_RECORDS = gql`
  query GetSingleMonthRemittanceRecords($input: GetSingleMonthRemittanceRecordsInput!) {
    getSingleMonthRemittanceRecords(input: $input) {
      entities {
        account_id
        payment_month
        current_balance_cents
        original_balance_cents
        maturity_date
        interest_rate_basis_points
        accrued_unpaid_interest_cents
        days_past_due
        starting_balance_cents
        payment_cents
        principal_cents
        interest_cents
        charge_off
        tdr
        bankruptcy
      }
    }
  }
`;

export type SingleMonthRemittanceRecordRow =
  GetSingleMonthRemittanceRecords_getSingleMonthRemittanceRecords_entities;

/**
 * Fetches the latest approved month of remittance records for a deal (or a specific month if provided).
 * When payment_month is omitted, the backend returns the latest approved month for the deal.
 * Uses cache-and-network so "View latest month" always hits the backend and empty state isn't stale cache.
 */
export function useGetSingleMonthRemittanceRecords(
  dealId: string | undefined,
  options?: { paymentMonth?: string | null; skip?: boolean },
) {
  const { paymentMonth, skip = false } = options ?? {};
  const validDealId = isValidDealId(dealId) ? dealId : undefined;
  return useQuery<GetSingleMonthRemittanceRecords, GetSingleMonthRemittanceRecordsVariables>(
    GET_SINGLE_MONTH_REMITTANCE_RECORDS,
    {
      variables: {
        input: {
          deal_id: validDealId ?? '',
          ...(paymentMonth != null && paymentMonth !== '' ? { payment_month: paymentMonth } : {}),
          pagination: { page_size: 500, offset: 0 },
        },
      },
      skip: !validDealId || skip,
      fetchPolicy: 'cache-and-network',
    },
  );
}
